import { useEffect, useRef, useCallback } from 'react';

const THROTTLE_MS = 10_000;
const TRACKED_EVENTS = ['mousemove', 'click', 'keydown', 'scroll'] as const;

const useActivityTracker = () => {
    const lastActivityRef = useRef<number>(Date.now());
    const lastRecordedRef = useRef<number>(Date.now());
    const onActivityRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const handleActivity = () => {
            const now = Date.now();
            lastActivityRef.current = now;

            if (now - lastRecordedRef.current >= THROTTLE_MS) {
                lastRecordedRef.current = now;
                onActivityRef.current?.();
            }
        };

        for (const event of TRACKED_EVENTS) {
            document.addEventListener(event, handleActivity, { passive: true });
        }

        return () => {
            for (const event of TRACKED_EVENTS) {
                document.removeEventListener(event, handleActivity);
            }
        };
    }, []);

    const wasActiveWithin = useCallback((windowMs: number) => Date.now() - lastActivityRef.current < windowMs, []);

    const setOnActivity = useCallback((callback: (() => void) | null) => {
        onActivityRef.current = callback;
    }, []);

    return { wasActiveWithin, setOnActivity };
};

export default useActivityTracker;
